import {
  deleteObject,
  FullMetadata,
  getBlob,
  getDownloadURL,
  getMetadata,
  getStorage,
  ref,
  StorageReference,
  uploadBytes,
  uploadBytesResumable,
} from 'firebase/storage';
import { firebaseApp } from 'firebaseConfig';
import { Dispatch, SetStateAction } from 'react';
import API from 'api/API';

/* -------------------------------------------------------------------------- */
/*                                   Images                                   */
/* -------------------------------------------------------------------------- */

export const getStorageRef = (url: string, addGCSPrefix: boolean): StorageReference => {
  if (addGCSPrefix) {
    return ref(getStorage(firebaseApp), `${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/${url}`);
  } else {
    // in some cases, the BE gives us back the REACT_APP_FIREBASE_STORAGE_BUCKET with the url
    // (like in the set and device attachments)
    return ref(getStorage(firebaseApp), url);
  }
};

export const getImage = async (url: string, addGCSPrefix: boolean): Promise<string> => {
  const imgURL = await getDownloadURL(getStorageRef(url, addGCSPrefix));

  return imgURL;
};

export const generateTrayReferenceImageName = (
  org: string,
  setCode: string,
  setRevision: string,
  trayRef: string,
  fileName: string,
): string => {
  // Generate the filename for the image - prefix it with region and tenant
  return `${org}/tray_reference_images/${setCode}_${setRevision}_${trayRef}_${fileName}`;
};

export const saveImageToGCS = async (imageName: string, setID: string, file: File): Promise<void> => {
  // Post the image to the backend
  await API.asset.post(`${setID}/${imageName}`, file);
};

export const doesImageExistInDatabase = async (url: string): Promise<boolean> => {
  try {
    await getDownloadURL(getStorageRef(url, true));
    return true;
  } catch {
    return false;
  }
};

export const saveImage = async (url: string, file: File): Promise<void> => {
  await uploadBytes(getStorageRef(url, true), file);
};

export const deleteImage = async (url: string): Promise<void> => {
  await deleteObject(getStorageRef(url, true));
};

export const getImageBlob = async (url: string): Promise<Blob> => {
  return await getBlob(getStorageRef(url, true));
};

/* -------------------------------------------------------------------------- */
/*                                   File                                   */
/* -------------------------------------------------------------------------- */
export const getFile = async (url: string, isFullPath: boolean, addGCSPrefix: boolean): Promise<string> => {
  let downloadUrl;

  if (isFullPath) {
    downloadUrl = await getDownloadURL(ref(getStorage(firebaseApp), url));
  } else if (!addGCSPrefix) {
    downloadUrl = await getDownloadURL(getStorageRef(url, false));
  } else {
    downloadUrl = await getDownloadURL(getStorageRef(url, true));
  }

  return downloadUrl;
};

export const readJSONFiles = async <Type>(url: string, isFullPath: boolean): Promise<Type> => {
  let blob;

  if (isFullPath) {
    blob = await getBlob(ref(getStorage(firebaseApp), url));
  } else {
    blob = await getBlob(getStorageRef(url, true));
  }

  const blobText = await blob.text();
  const jsonObj = JSON.parse(blobText);

  return jsonObj as Type;
};

export const saveFileWithProgress = async (
  url: string,
  file: File,
  setProgress?: Dispatch<SetStateAction<number>>,
): Promise<void> => {
  const storageRef = getStorageRef(url, true);
  const uploadTask = uploadBytesResumable(storageRef, file);

  uploadTask.on(
    'state_changed',
    snapshot => {
      const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;

      if (progress && setProgress) {
        setProgress(progress);
      }
    },
    error => {
      API.errorHandler(error);
    },
  );
};

export const saveFile = async (url: string, file: File): Promise<void> => {
  await uploadBytes(getStorageRef(url, true), file);
};

export const getFileMetadata = async (url: string): Promise<FullMetadata> => {
  return getMetadata(ref(getStorage(firebaseApp), `${url}`))
    .then(data => {
      return data;
    })
    .catch(error => {
      return error;
    });
};

export const getGSPath = (url: string) => {
  const storageRef = getStorageRef(url, true);

  return `gs://${storageRef.bucket}/${storageRef.fullPath}`;
};

/* -------------------------------------------------------------------------- */
/*                                   Videos                                   */
/* -------------------------------------------------------------------------- */

export const getVideo = async (url: string, useConverted: boolean): Promise<string> => {
  const videoURL = await getDownloadURL(
    ref(
      getStorage(firebaseApp),
      `${
        useConverted ? process.env.REACT_APP_GOOGLE_CONVERTED_VIDEO_BUCKET : process.env.REACT_APP_FIREBASE_VIDEO_BUCKET
      }/${url}`,
    ),
  );
  return videoURL;
};

export const ImageService = {
  saveImage,
  deleteImage,
  getImage,
  getImageBlob,
  doesImageExistInDatabase,
};

export const FileService = {
  getFile,
  saveFile,
  saveFileWithProgress,
  getFileMetadata,
};
